/**
 * PushNotificationService
 * Service for push notification operations
 * Route: api/pushnotification
 */
import { BaseService } from '../base/BaseService';
/**
 * PushNotificationService
 * Handles push notification sending and management operations
 */
export class PushNotificationService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PushNotificationService.DEFAULT_SERVICE_PATH,
            basePath: PushNotificationService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== MESSAGE OPERATIONS ====================
    /**
     * Get all push messages with caching
     * GET api/pushnotification/messages
     */
    async getMessages() {
        const response = await this.getMessagesRaw();
        return response.data ?? [];
    }
    /**
     * Get messages with full response
     */
    getMessagesRaw() {
        const endpoint = this.getEndpoint('messages', 'messages');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get push message by ID
     * GET api/pushnotification/messages/{id}
     */
    async getMessageById(id) {
        const response = await this.getMessageByIdRaw(id);
        if (!response.data) {
            throw new Error(`Push message ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get message by ID with full response
     */
    getMessageByIdRaw(id) {
        const endpoint = this.getEndpoint('message', `messages/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create a new push message
     * POST api/pushnotification/messages
     */
    async createMessage(message) {
        const response = await this.createMessageRaw(message);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create push message');
        }
        return response.data;
    }
    /**
     * Create message with full response
     */
    createMessageRaw(message) {
        const endpoint = this.getEndpoint('messages', 'messages');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, message);
    }
    /**
     * Update an existing push message
     * PUT api/pushnotification/messages/{id}
     */
    async updateMessage(id, message) {
        const response = await this.updateMessageRaw(id, message);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update push message');
        }
        return response.data;
    }
    /**
     * Update message with full response
     */
    updateMessageRaw(id, message) {
        const endpoint = this.getEndpoint('message-update', `messages/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, message);
    }
    /**
     * Delete a push message
     * DELETE api/pushnotification/messages/{id}
     */
    async deleteMessage(id) {
        const response = await this.deleteMessageRaw(id);
        return response.success;
    }
    /**
     * Delete message with full response
     */
    deleteMessageRaw(id) {
        const endpoint = this.getEndpoint('message-delete', `messages/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    /**
     * Get all push messages as model views
     * GET api/pushnotification/messages/view
     */
    async getMessageViews() {
        const response = await this.getMessageViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get message views with full response
     */
    getMessageViewsRaw() {
        const endpoint = this.getEndpoint('messages-view', 'messages/view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get push message model view by ID
     * GET api/pushnotification/messages/view/{id}
     */
    async getMessageViewById(id) {
        const response = await this.getMessageViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Push message ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get message view by ID with full response
     */
    getMessageViewByIdRaw(id) {
        const endpoint = this.getEndpoint('message-view', `messages/view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== NOTIFICATION OPERATIONS ====================
    /**
     * Get all push notifications
     * GET api/pushnotification/notifications
     */
    async getNotifications() {
        const response = await this.getNotificationsRaw();
        return response.data ?? [];
    }
    /**
     * Get notifications with full response
     */
    getNotificationsRaw() {
        const endpoint = this.getEndpoint('notifications', 'notifications');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get pending push notifications
     * GET api/pushnotification/notifications/pending
     */
    async getPendingNotifications() {
        const response = await this.getPendingNotificationsRaw();
        return response.data ?? [];
    }
    /**
     * Get pending notifications with full response
     */
    getPendingNotificationsRaw() {
        const endpoint = this.getEndpoint('notifications-pending', 'notifications/pending');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create a new push notification
     * POST api/pushnotification/notifications
     */
    async createNotification(notification) {
        const response = await this.createNotificationRaw(notification);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create push notification');
        }
        return response.data;
    }
    /**
     * Create notification with full response
     */
    createNotificationRaw(notification) {
        const endpoint = this.getEndpoint('notifications', 'notifications');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, notification);
    }
    /**
     * Mark notification as created
     * POST api/pushnotification/notifications/{id}/mark-created
     */
    async markNotificationCreated(id) {
        const response = await this.markNotificationCreatedRaw(id);
        return response.success;
    }
    /**
     * Mark notification as created with full response
     */
    markNotificationCreatedRaw(id) {
        const endpoint = this.getEndpoint('notification-mark-created', `notifications/${id}/mark-created`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
    /**
     * Get all push notifications as model views
     * GET api/pushnotification/notifications/view
     */
    async getNotificationViews() {
        const response = await this.getNotificationViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get notification views with full response
     */
    getNotificationViewsRaw() {
        const endpoint = this.getEndpoint('notifications-view', 'notifications/view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get push notification model view by ID
     * GET api/pushnotification/notifications/view/{id}
     */
    async getNotificationViewById(id) {
        const response = await this.getNotificationViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Push notification ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get notification view by ID with full response
     */
    getNotificationViewByIdRaw(id) {
        const endpoint = this.getEndpoint('notification-view', `notifications/view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== TYPE OPERATIONS ====================
    /**
     * Get all notification types
     * GET api/pushnotification/types
     */
    async getTypes() {
        const response = await this.getTypesRaw();
        return response.data ?? [];
    }
    /**
     * Get types with full response
     */
    getTypesRaw() {
        const endpoint = this.getEndpoint('types', 'types');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create a new notification type
     * POST api/pushnotification/types
     */
    async createType(type) {
        const response = await this.createTypeRaw(type);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create notification type');
        }
        return response.data;
    }
    /**
     * Create type with full response
     */
    createTypeRaw(type) {
        const endpoint = this.getEndpoint('types', 'types');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, type);
    }
    // ==================== CACHE OPERATIONS ====================
    /**
     * Invalidate push notification cache
     * POST api/pushnotification/cache/invalidate
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate cache with full response
     */
    invalidateCacheRaw() {
        const endpoint = this.getEndpoint('cache-invalidate', 'cache/invalidate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
}
PushNotificationService.DEFAULT_SERVICE_PATH = 'communications';
PushNotificationService.BASE_PATH = 'api/pushnotification';
//# sourceMappingURL=PushNotificationService.js.map